import { User, UserFormData, BankInfoFormData, HourlyRateFormData, UserLevelFormData, PasswordResetFormData, HourlyRateHistory, UserLevelHistory } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

// 取得認證標頭
const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  }
}

// 處理 API 回應
const handleResponse = async (response: Response, context?: string) => {
  // 添加詳細的調試資訊
  console.log(`API ${context || 'Request'}:`, {
    url: response.url,
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries())
  })

  if (!response.ok) {
    let errorData: any = {}
    let errorText = ''
    
    try {
      // 嘗試解析 JSON 錯誤回應
      const responseText = await response.text()
      errorText = responseText
      
      if (responseText) {
        errorData = JSON.parse(responseText)
      }
    } catch (parseError) {
      console.error('Failed to parse error response:', parseError)
    }
    
    // 記錄詳細錯誤資訊
    console.error('API Error Details:', {
      url: response.url,
      status: response.status,
      statusText: response.statusText,
      errorData,
      rawResponse: errorText,
      context
    })
    
    // 建立詳細的錯誤物件
    const error = new Error()
    error.message = errorData.message || `HTTP ${response.status}: ${response.statusText}`
    
    // 添加額外的錯誤資訊
    ;(error as any).status = response.status
    ;(error as any).statusText = response.statusText
    ;(error as any).url = response.url
    ;(error as any).errorData = errorData
    ;(error as any).rawResponse = errorText
    ;(error as any).context = context
    
    throw error
  }
  
  const data = await response.json()
  console.log(`API ${context || 'Response'} Success:`, data)
  return data
}

export const userService = {
  // 取得用戶列表
  async getUsers(params?: {
    role?: string
    isActive?: boolean
    search?: string
  }): Promise<User[]> {
    const searchParams = new URLSearchParams()
    
    if (params?.role) {
      searchParams.append('role', params.role)
    }
    if (params?.isActive !== undefined) {
      searchParams.append('isActive', params.isActive.toString())
    }
    if (params?.search) {
      searchParams.append('search', params.search)
    }

    const response = await fetch(
      `${API_BASE_URL}/users?${searchParams.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    )

    const result = await handleResponse(response, 'Get Users')
    return result.data
  },

  // 取得單一用戶詳情
  async getUser(userId: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    const result = await handleResponse(response, 'Get User')
    return result.data
  },

  // 創建新用戶
  async createUser(userData: UserFormData): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    })

    const result = await handleResponse(response, 'Create User')
    return result.data
  },

  // 更新用戶資訊
  async updateUser(userId: string, userData: Partial<UserFormData>): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    })

    const result = await handleResponse(response, 'Update User')
    return result.data
  },

  // 更新用戶狀態
  async updateUserStatus(userId: string, isActive: boolean): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ isActive })
    })

    const result = await handleResponse(response, 'Update User Status')
    return result.data
  },

  // 重設用戶密碼
  async resetPassword(userId: string, passwordData: PasswordResetFormData): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ newPassword: passwordData.newPassword })
    })

    await handleResponse(response, 'Reset Password')
  },

  // 更新銀行資訊
  async updateBankInfo(userId: string, bankInfo: BankInfoFormData): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/bank-info`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(bankInfo)
    })

    const result = await handleResponse(response, 'Update Bank Info')
    return result.data
  },

  // 刪除銀行資訊
  async deleteBankInfo(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/bank-info`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    })

    await handleResponse(response, 'Delete Bank Info')
  },

  // 更新時薪
  async updateHourlyRate(userId: string, rateData: HourlyRateFormData): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/hourly-rate`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(rateData)
    })

    const result = await handleResponse(response, 'Update Hourly Rate')
    return result.data
  },

  // 取得時薪變更歷史
  async getHourlyRateHistory(userId: string): Promise<HourlyRateHistory[]> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/hourly-rate-history`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    const result = await handleResponse(response, 'Get Hourly Rate History')
    return result.data
  },

  // 更新用戶職等
  async updateUserLevel(userId: string, levelData: UserLevelFormData): Promise<User> {
    console.log('Frontend: Initiating user level update', {
      userId,
      levelData,
      timestamp: new Date().toISOString()
    })

    const response = await fetch(`${API_BASE_URL}/users/${userId}/level`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(levelData)
    })

    console.log('Frontend: Received response from level update', {
      userId,
      status: response.status,
      statusText: response.statusText,
      timestamp: new Date().toISOString()
    })

    const result = await handleResponse(response, 'Update User Level')
    
    console.log('Frontend: Level update completed successfully', {
      userId,
      newLevel: result.data?.level,
      timestamp: new Date().toISOString()
    })
    
    return result.data
  },

  // 取得職等變更歷史
  async getUserLevelHistory(userId: string): Promise<UserLevelHistory[]> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/level-history`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    const result = await handleResponse(response, 'Get User Level History')
    return result.data
  }
}
